import { Stack, Typography, IconButton } from "@mui/material";
import { CustomCellRendererProps } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { globalTheme } from "../../styles";
import { CustomerProfileIcon, EditIcon } from "../Icon";
import { EmailOutlined, PhonelinkLockOutlined, EditOutlined } from "@mui/icons-material";
import { CustomerEditDrawer } from "./OrderCustomerEditDrawer/OrderCustomerEditDrawer";
import { useUpdateAction } from "../../hooks";
import { AUTHENTICATED_EMPLOYEE_JWT } from "../../constants";

interface OrdersCustomerDataCell extends CustomCellRendererProps {
  refreshData: () => void;
  setSnackBar: (snackBar: { open: boolean; message: string; severity: string }) => void;
}

export const OrdersCustomerDataCell = React.memo((params: OrdersCustomerDataCell) => {
  const { data, refreshData } = params;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [actionUpdateRequest, setActionUpdateRequest] = useState({ requestAvailable: false, request: [] });
  const [actionRequest, setActionRequest] = useState<{
    actionType: string;
    idList: string[];
    changes: { fieldName: string; oldValue: string; newValue: string | number }[];
  }>({
    actionType: "",
    idList: [],
    changes: [{ fieldName: "", oldValue: "", newValue: "" }],
  });

  const { successfullUpdatedAction, updateActionResponseData } = useUpdateAction({
    updateActionRequest: actionUpdateRequest.request,
    enabled: actionUpdateRequest.requestAvailable,
  });

  useEffect(() => {
    if (updateActionResponseData?.code === "UNAUTHORIZED") {
      localStorage.removeItem(AUTHENTICATED_EMPLOYEE_JWT);
      window.location.reload();
    }
  }, [updateActionResponseData]);

  const handleEditClick = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const localDateTime = new Date(data.appImportDateTime).toLocaleString();

  const [updateAPIEnable, setUpdateAPIEnabled] = useState<boolean>(false);

  useEffect(() => {
    setActionUpdateRequest({ requestAvailable: updateAPIEnable, request: [actionRequest] });
  }, [updateAPIEnable]);

  // Integrate with UPdate Api here
  const handleOnUpdateCustomerData = (updatedFormData) => {
    setActionRequest({
      actionType: "ADDRESS_CHANGE",
      idList: data.orderIds,
      changes: [
        { fieldName: "shippingFirstName", oldValue: "", newValue: updatedFormData.shippingFirstName },
        { fieldName: "shippingLastName", oldValue: "", newValue: updatedFormData.shippingLastName },
        { fieldName: "shippingCompanyName", oldValue: "", newValue: updatedFormData.shippingCompanyName },
        { fieldName: "shippingDaytimePhone", oldValue: "", newValue: updatedFormData.shippingDaytimePhone },
        { fieldName: "buyerEmailAddress", oldValue: "", newValue: updatedFormData.buyerEmailAddress },
        { fieldName: "shippingAddressLine1", oldValue: "", newValue: updatedFormData.shippingAddressLine1 },
        { fieldName: "shippingAddressLine2", oldValue: "", newValue: updatedFormData.shippingAddressLine2 },
        { fieldName: "shippingCity", oldValue: "", newValue: updatedFormData.shippingCity },
        { fieldName: "shippingPostalCode", oldValue: "", newValue: updatedFormData.shippingPostalCode },
        { fieldName: "shippingStateOrProvince", oldValue: "", newValue: updatedFormData.shippingStateOrProvince },
        { fieldName: "shippingCountry", oldValue: "", newValue: updatedFormData.shippingCountry },
      ],
    });
    setUpdateAPIEnabled(true);
  };

  useEffect(() => {
    if (updateActionResponseData) {
      refreshData();
      setUpdateAPIEnabled(false);
    }
  }, [updateActionResponseData]);

  return useMemo(
    () => (
      <>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack flex={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CustomerProfileIcon />
              <Stack>
                <Stack direction="row" spacing={0.5}>
                  {(data.shippingFirstName || data.shippingLastName || data?.shippingCompanyName) && (
                    <Typography variant="body2" color={globalTheme.primaryColor}>
                      {`${data.shippingFirstName || ""} ${data.shippingLastName || ""}${
                        data.shippingCompanyName ? `, ${data.shippingCompanyName}` : ""
                      }`}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  {(data.shippingAddressLine1 || data?.shippingAddressLine2) && (
                    <Typography variant="caption" color={globalTheme.primaryColor}>
                      {`${data.shippingAddressLine1 || ""}${data.shippingAddressLine2 ? `, ${data.shippingAddressLine2}` : ""}`}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  {data.shippingCity && (
                    <Typography variant="caption" color={globalTheme.primaryColor}>
                      {`${data.shippingCity || ""},`}
                    </Typography>
                  )}
                  {data.shippingStateOrProvince && (
                    <Typography variant="caption" color={globalTheme.primaryColor}>
                      {`${data.shippingStateOrProvince || ""},`}
                    </Typography>
                  )}
                  {(data.shippingCountry || data.shippingPostalCode) && (
                    <Typography variant="caption" color={globalTheme.primaryColor}>
                      {` ${data.shippingCountry || ""}, ${data.shippingPostalCode || ""}`}
                    </Typography>
                  )}
                </Stack>

                {(data.shippingDaytimePhone || data.buyerEmailAddress) && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {data.shippingDaytimePhone && (
                      <Typography
                        variant="caption"
                        color={globalTheme.primaryColor}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <PhonelinkLockOutlined sx={{ fontSize: 14, marginRight: 0.5 }} /> {data.shippingDaytimePhone}
                      </Typography>
                    )}
                    {data.buyerEmailAddress && (
                      <Typography
                        variant="caption"
                        color={globalTheme.primaryColor}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <EmailOutlined sx={{ fontSize: 14, marginLeft: 1, marginRight: 0.5 }} /> {data.buyerEmailAddress}
                      </Typography>
                    )}
                  </Stack>
                )}

                {data?.siteName && (
                  <Typography variant="body2">
                    <strong style={{ color: "#01168e", fontSize: "0.7rem" }}>{data?.siteName}</strong>
                    {" - "}
                    {localDateTime}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Edit Icon */}
          {data?.siteName !== "Amazon WI" && (
            <IconButton size="medium" onClick={handleEditClick}>
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        {/* Drawer Component */}
        <CustomerEditDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          customerData={data}
          OnUpdatedCustomerData={handleOnUpdateCustomerData}
        />
      </>
    ),
    [data, drawerOpen]
  );
});
